import { useDragControls } from "framer-motion";
import { useState } from "react";
import AdminSidebar from "../components/admin/AdminSidebar";
import BannerManagement from "../components/admin/BannerManagement";
import CategoryManagement from "../components/admin/CategoryManagement";
import CouponManagement from "../components/admin/CouponManagement";
import DashboardStats from "../components/admin/DashboardStats";
import EmailTemplateManagement from "../components/admin/EmailTemplateManagement";
import FeaturedTemplatesManagement from "../components/admin/FeaturedTemplatesManagement";
import FestiveSpecialsManagement from "../components/admin/FestiveSpecialsManagement";
import HotSellingManagement from "../components/admin/HotSellingManagement";
import InhousePublicationsManagement from "../components/admin/InhousePublicationsManagement";
import OrderManagement from "../components/admin/OrderManagement";
import ProductManagement from "../components/admin/ProductManagement";
import Reports from "../components/admin/Reports";
import SiteSettings from "../components/admin/SiteSettings";
import TemplateManagement from "../components/admin/TemplateManagement";
import UserManagement from "../components/admin/UserManagement";
import { AdminProvider } from "../contexts/AdminContext";

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState("dashboard");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dragControls = useDragControls();

    // Handle swipe gestures for opening mobile menu
    const handleMainDragEnd = (event, info) => {
        const swipeThreshold = 100;
        const velocityThreshold = 500;

        // Swipe right to open sidebar (when closed)
        if (!mobileMenuOpen && (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold)) {
            setMobileMenuOpen(true);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case "dashboard":
                return <DashboardStats />;
            case "products":
                return <ProductManagement />;
            case "templates":
                return <TemplateManagement />;
            case "users":
                return <UserManagement />;
            case "orders":
                return <OrderManagement />;
            case "categories":
                return <CategoryManagement />;
            case "festive-specials":
                return <FestiveSpecialsManagement />;
            case "featured-templates":
                return <FeaturedTemplatesManagement />;
            case "inhouse-publications":
                return <InhousePublicationsManagement />;
            case "hot-selling":
                return <HotSellingManagement />;
            case "banners":
                return <BannerManagement />;
            case "coupons":
                return <CouponManagement />;
            case "reports":
                return <Reports />;
            case "settings":
                return <SiteSettings />;
            case "email-templates":
                return <EmailTemplateManagement />;
            default:
                return <DashboardStats />;
        }
    };

    const getPageTitle = () => {
        switch (activeTab) {
            case "dashboard":
                return "Dashboard Overview";
            case "reports":
                return "Reports & Analytics";
            default:
                return `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Management`;
        }
    };

    const getPageDescription = () => {
        switch (activeTab) {
            case "dashboard":
                return "Monitor your business performance and key metrics";
            case "products":
                return "Manage your product catalog and inventory";
            case "templates":
                return "Create and customize design templates";
            case "users":
                return "Manage customer accounts and user permissions";
            case "orders":
                return "Track and process customer orders";
            case "categories":
                return "Manage product, design, and template categories";
            case "festive-specials":
                return "Manage products displayed in Festive Specials section";
            case "featured-templates":
                return "Manage templates displayed in Design Templates section";
            case "inhouse-publications":
                return "Manage products displayed in Inhouse Titles section";
            case "coupons":
                return "Create and manage promotional codes";
            case "reports":
                return "Analyze business data and generate insights";
            case "settings":
                return "Configure site status and system settings";
            case "email-templates":
                return "Manage email templates for automated communications";
            default:
                return "Manage your Kigo Prints platform";
        }
    };

    return (
        <AdminProvider>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50">
            {/* Mobile Overlay */}
            {mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            <AdminSidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                mobileMenuOpen={mobileMenuOpen}
                setMobileMenuOpen={setMobileMenuOpen}
            />

            {/* Main Content Area */}
            <div className="lg:ml-72">
                {/* Mobile Header Bar */}
                <div className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
                    <div className="px-4 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => setMobileMenuOpen(true)}
                                    className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
                                >
                                    <svg
                                        className="w-5 h-5 text-gray-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                    </svg>
                                </button>
                                <div>
                                    <h1 className="text-lg font-bold text-gray-900">{getPageTitle()}</h1>
                                </div>
                            </div>

                            {/* Mobile Actions */}
                            <div className="flex items-center space-x-2">
                                <div className="relative">
                                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            {/* Desktop Header */}
            <div className="hidden lg:block sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm">
                <div className="px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent">
                                {getPageTitle()}
                            </h1>
                            <p className="text-gray-600 mt-1 text-sm lg:text-base">
                                {getPageDescription()}
                            </p>
                        </div>

                            {/* Desktop Quick Actions */}
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                                        <div className="w-2 h-2 bg-white rounded-full"></div>
                                    </div>
                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                                </div>
                                <div className="hidden sm:block text-right">
                                    <p className="text-sm font-medium text-gray-900">Admin Panel</p>
                                    <p className="text-xs text-gray-500">Kigo Prints</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Page Content */}
                <div className="p-4 sm:p-6 lg:p-8">
                    <div className="max-w-7xl mx-auto">
                        <div key={activeTab}>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </AdminProvider>
    );
};

export default AdminDashboard;
